import { useEffect, useState } from "react";
import { Play, Square, RotateCcw, Power, Search, AlertTriangle, RefreshCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const FILTERS = [
  { k: "all", label: "Alle" },
  { k: "running", label: "Aktiv" },
  { k: "failed", label: "Failed" },
  { k: "exited", label: "Exited" },
];

const ACTIONS = [
  { k: "start", label: "Start", icon: Play, color: "success" },
  { k: "stop", label: "Stop", icon: Square, color: "danger" },
  { k: "restart", label: "Restart", icon: RotateCcw, color: "primary" },
  { k: "reload", label: "Reload", icon: RefreshCcw, color: "neutral" },
  { k: "enable", label: "Enable", icon: Power, color: "neutral" },
  { k: "disable", label: "Disable", icon: Power, color: "neutral" },
];

export default function Services() {
  const [items, setItems] = useState([]);
  const [state, setState] = useState("all");
  const [pattern, setPattern] = useState("");
  const [available, setAvailable] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [logs, setLogs] = useState(null);

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/services", { params: { state, pattern } });
      setItems(data.items);
      setAvailable(data.available);
      if (!data.available && data.error) setError(data.error);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [state]);

  const act = async (name, action) => {
    setNotice("");
    try {
      const { data } = await api.post(`/services/${encodeURIComponent(name)}/action`, { action });
      setNotice(`${action} → ${name} · ok`);
      if (selected === name) openDetail(name);
      load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const openDetail = async (name) => {
    setSelected(name);
    setDetail(null);
    setLogs(null);
    try {
      const [d, l] = await Promise.all([
        api.get(`/services/${encodeURIComponent(name)}`),
        api.get(`/services/${encodeURIComponent(name)}/logs`, { params: { lines: 100 } }),
      ]);
      setDetail(d.data);
      setLogs(l.data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const activeColor = (sub) =>
    sub === "running" ? "text-[#10b981]" : sub === "failed" ? "text-[#dc2626]" : "text-[#8b8d98]";

  return (
    <div data-testid="services-page">
      <PageHeader title="Services" subtitle="// systemd" testId="services-header">
        <button
          onClick={load}
          data-testid="services-refresh-button"
          className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] border border-[#1f1f22] text-[#8b8d98] hover:text-white hover:border-white"
        >
          <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
          Aktualisieren
        </button>
      </PageHeader>

      <div className="px-8 py-4 flex items-center gap-3 flex-wrap grid-border-b" style={{ borderBottom: "1px solid #1f1f22" }}>
        <div className="flex items-center gap-2 bg-black border border-[#1f1f22] px-2 py-1.5 flex-1 max-w-sm focus-within:border-[#22d3ee]">
          <Search className="w-3.5 h-3.5 text-[#8b8d98]" strokeWidth={1.5} />
          <input
            data-testid="services-search"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Service-Filter: nginx, ssh…"
            className="bg-transparent outline-none w-full font-mono text-xs text-white placeholder-[#3f3f46]"
          />
        </div>
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.k}
              onClick={() => setState(f.k)}
              data-testid={`services-filter-${f.k}`}
              className={[
                "px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] border",
                state === f.k
                  ? "bg-[#22d3ee]/10 border-[#22d3ee] text-[#22d3ee]"
                  : "border-[#1f1f22] text-[#8b8d98] hover:text-white",
              ].join(" ")}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!available ? (
        <div className="mx-8 mt-4 flex items-start gap-2 text-xs font-mono text-[#eab308] border border-[#eab308]/40 bg-[#eab308]/5 px-3 py-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" strokeWidth={1.5} />
          <div>
            <div className="font-medium mb-1">systemd nicht verfügbar in dieser Umgebung</div>
            <div className="text-[#8b8d98]">
              Auf deinem Debian-Server (monitoring.armesa.de) funktionieren alle Aktionen
              normal. Details: {error}
            </div>
          </div>
        </div>
      ) : null}

      {notice ? (
        <div className="mx-8 mt-4 text-xs font-mono text-[#10b981] border border-[#10b981]/40 bg-[#10b981]/5 px-3 py-2">
          {notice}
        </div>
      ) : null}
      {error && available ? (
        <div className="mx-8 mt-4 text-xs font-mono text-[#dc2626] border border-[#dc2626]/40 bg-[#dc2626]/5 px-3 py-2">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#1f1f22] m-8">
        <div className="lg:col-span-2 bg-[#0a0a0a]">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 overline grid-border-b" style={{ borderBottom: "1px solid #1f1f22" }}>
            <div className="col-span-4">Unit</div>
            <div className="col-span-1">Load</div>
            <div className="col-span-1">Active</div>
            <div className="col-span-1">Sub</div>
            <div className="col-span-5">Beschreibung</div>
          </div>
          <div className="divide-y divide-[#1f1f22] max-h-[70vh] overflow-auto">
            {items.map((s) => (
              <button
                key={s.unit}
                onClick={() => openDetail(s.unit)}
                data-testid={`service-row-${s.unit}`}
                className={[
                  "w-full text-left grid grid-cols-12 gap-2 px-4 py-2 items-center font-mono text-[11px] row-hover",
                  selected === s.unit ? "bg-[#111111]" : "",
                ].join(" ")}
              >
                <div className="col-span-4 text-white truncate">{s.unit}</div>
                <div className="col-span-1 text-[#8b8d98]">{s.load}</div>
                <div className="col-span-1 text-[#8b8d98]">{s.active}</div>
                <div className={`col-span-1 ${activeColor(s.sub)}`}>{s.sub}</div>
                <div className="col-span-5 text-[#8b8d98] truncate">{s.description}</div>
              </button>
            ))}
            {!items.length && (
              <div className="px-4 py-10 text-xs font-mono text-[#8b8d98] text-center">
                Keine Services gefunden.
              </div>
            )}
          </div>
        </div>

        {/* Detail Panel */}
        <div className="bg-[#0a0a0a] p-4">
          {!selected ? (
            <div className="text-xs font-mono text-[#8b8d98]">
              Wähle einen Service für Details & Aktionen.
            </div>
          ) : (
            <div data-testid="service-detail">
              <div className="overline mb-2">// {selected}</div>
              <div className="flex flex-wrap gap-1 mb-4">
                {ACTIONS.map(({ k, label, icon: Icon, color }) => (
                  <button
                    key={k}
                    onClick={() => act(selected, k)}
                    data-testid={`service-action-${k}`}
                    className={[
                      "flex items-center gap-1 px-2 py-1.5 text-[11px] font-mono uppercase tracking-[0.1em] border",
                      color === "danger"
                        ? "border-[#dc2626]/60 text-[#dc2626] hover:bg-[#dc2626] hover:text-white"
                        : color === "success"
                        ? "border-[#10b981]/60 text-[#10b981] hover:bg-[#10b981] hover:text-black"
                        : color === "primary"
                        ? "border-[#22d3ee]/60 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-black"
                        : "border-[#1f1f22] text-[#8b8d98] hover:text-white hover:border-white",
                    ].join(" ")}
                  >
                    <Icon className="w-3 h-3" strokeWidth={1.5} />
                    {label}
                  </button>
                ))}
              </div>

              {detail ? (
                <pre className="text-[11px] font-mono text-[#8b8d98] whitespace-pre-wrap bg-black p-3 border border-[#1f1f22] max-h-60 overflow-auto">
                  {detail.status_text || "—"}
                </pre>
              ) : (
                <div className="text-xs font-mono text-[#8b8d98] animate-pulse">Lade…</div>
              )}

              <div className="overline mt-4 mb-2">// journalctl -n 100</div>
              {logs ? (
                <pre className="text-[11px] font-mono text-[#8b8d98] whitespace-pre-wrap bg-black p-3 border border-[#1f1f22] max-h-60 overflow-auto">
                  {logs.stdout || logs.stderr || "—"}
                </pre>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
